import React, { useState, useEffect, useRef } from "react";
import {
  createInward,
  createOutward,
  updateInward,
  updateOutward,
  fetchDocumentTypes,
  fetchDispatchModes,
} from "../api/inwardOutwardApi";
import { getCsrfToken } from "../../../public/api/authApi";
import {
  GlobalButton,
  GlobalSelectField,
  GlobalTextAreaField,
  GlobalTextField,
} from "../../../global/forms/GlobalFormFields";
import { gfValidateNameTextField } from "../../../global/forms/globalFormValidation";
import UserSearch from "./UserSearch";
import GlobalFormShell from "../../../global/forms/GlobalFormShell";
import { masterSelectOptions, filterActiveMasterRows } from "../../memberships/utils/masterLookupUtils";
import {
  INWARD_OUTWARD_TEXT_MAX,
  ALLOWED_UPLOAD_TYPES,
  INVALID_UPLOAD_TYPE_MESSAGE,
  validateInwardOutwardTextField,
} from "../utils/inwardOutwardFormConstants";
import { compressAttachmentIfImage } from "../../../utils/compressImage";
import { resolveMediaUrl } from "../../../utils/resolveMediaUrl";

const EMPTY_FORM = {
  // Shared
  document_type_id: "",
  subject: "",
  dispatch_mode_id: "",
  remarks: "",
  attachment: "",
  
  // Inward specific
  date_received: new Date().toISOString().split("T")[0],
  from_name: "",
  from_address: "",
  received_by: "",
  
  // Outward specific
  date_sent: new Date().toISOString().split("T")[0],
  to_name: "",
  to_address: "",
  dispatched_by: "",
  acknowledgement_no: "",
};
const buildAttachmentUrl = (attachmentPath) => resolveMediaUrl(attachmentPath);

const getAttachmentName = (attachmentPath) => {
  if (!attachmentPath || typeof attachmentPath !== "string") return "";
  const cleanPath = attachmentPath.split("?")[0];
  const parts = cleanPath.split("/");
  return parts[parts.length - 1] || cleanPath;
};

const formatDateForInput = (value) => {
  if (!value) return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value.slice(0, 10);
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const RegisterEntryForm = ({
  onSuccess,
  lookupsRefreshKey = 0,
  editingRecord,
  onCancelEdit,
  defaultRegisterType = "inward",
  apiScope,
}) => {
  const [registerType, setRegisterType] = useState(defaultRegisterType); // "inward" or "outward"
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [attachment, setAttachment] = useState(null);
  const [error, setError] = useState(null);
  const [errorDetails, setErrorDetails] = useState({});
  const [loading, setLoading] = useState(false);
  
  const [lookups, setLookups] = useState({
    documentTypes: [],
    dispatchModes: [],
  });

  const alertRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const loadLookups = async () => {
      try {
        // Masters only — do not couple to user-builder (that permission is separate).
        const [docsRes, dispatchRes] = await Promise.all([
          fetchDocumentTypes({ scope: "dropdown" }),
          fetchDispatchModes({ scope: "dropdown" }),
        ]);
        setLookups({
          documentTypes: filterActiveMasterRows(docsRes.data || []),
          dispatchModes: filterActiveMasterRows(dispatchRes.data || []),
        });
      } catch (err) {
        console.error("Failed to load lookups for register form", err);
      }
    };
    loadLookups();
  }, [lookupsRefreshKey]);

  useEffect(() => {
    if (editingRecord) {
      const isOutward = !!editingRecord.outward_no;
      setRegisterType(isOutward ? "outward" : "inward");
      setFormData({
        ...EMPTY_FORM,
        ...editingRecord,
        date_received: formatDateForInput(editingRecord.date_received),
        date_sent: formatDateForInput(editingRecord.date_sent),
      });
      setAttachment(null);
      setError(null);
      setErrorDetails({});
    } else {
      setRegisterType(defaultRegisterType);
      handleReset();
    }
  }, [editingRecord, defaultRegisterType]);

  const setFormField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errorDetails[name]) {
      setErrorDetails((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleReset = () => {
    setError(null);
    setErrorDetails({});
    setFormData({ ...EMPTY_FORM });
    setAttachment(null);
    // Clear the file input field
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const validateDateNotFuture = (dateString) => {
    if (!dateString) return true;
    const inputDate = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    inputDate.setHours(0, 0, 0, 0);
    return inputDate <= today;
  };

  const validateAll = () => {
    const errors = {};
    if (!formData.document_type_id) errors.document_type_id = "Required";
    const subjectErr = validateInwardOutwardTextField(formData.subject, "Subject");
    if (subjectErr) errors.subject = subjectErr;
    if (!formData.dispatch_mode_id) errors.dispatch_mode_id = "Required";
    const remarksErr = validateInwardOutwardTextField(formData.remarks, "Remarks");
    if (remarksErr) errors.remarks = remarksErr;
    if (!attachment && !editingRecord) errors.attachment = "Required";
    if (!attachment && editingRecord && !editingRecord.attachment) {
      errors.attachment = "Required";
    }
    
    if (registerType === "inward") {
      if (!formData.date_received) errors.date_received = "Required";
      else if (!validateDateNotFuture(formData.date_received)) errors.date_received = "Date cannot be in the future";
      if (!formData.from_name?.trim()) errors.from_name = "Required";
      const fromAddressErr = validateInwardOutwardTextField(formData.from_address, "From address");
      if (fromAddressErr) errors.from_address = fromAddressErr;
      if (!formData.received_by) errors.received_by = "Required";
    } else {
      if (!formData.date_sent) errors.date_sent = "Required";
      else if (!validateDateNotFuture(formData.date_sent)) errors.date_sent = "Date cannot be in the future";
      const toName = String(formData.to_name || "").trim();
      if (!toName) {
        errors.to_name = "Required";
      } else if (toName.length > 30) {
        errors.to_name = "Maximum 30 characters.";
      } else {
        const nameError = gfValidateNameTextField(toName);
        if (nameError) errors.to_name = nameError;
      }
      const toAddressErr = validateInwardOutwardTextField(formData.to_address, "To address");
      if (toAddressErr) errors.to_address = toAddressErr;
      if (!formData.dispatched_by) errors.dispatched_by = "Required";
    }
    
    setErrorDetails(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setError(null);
    setErrorDetails({});
    
    if (!validateAll()) {
      setError("Please check the highlighted fields below.");
      setTimeout(() => alertRef.current?.focus(), 0);
      return;
    }

    setLoading(true);
    try {
      let csrfToken = localStorage.getItem("csrf-token");
      if (!csrfToken) {
        csrfToken = await getCsrfToken();
        localStorage.setItem("csrf-token", csrfToken);
      }
      
      let payload = { ...formData };
      
      // Clean up payload based on registerType
      if (registerType === "inward") {
        delete payload.date_sent;
        delete payload.to_name;
        delete payload.to_address;
        delete payload.dispatched_by;
        delete payload.acknowledgement_no;
      } else {
        delete payload.date_received;
        delete payload.from_name;
        delete payload.from_address;
        delete payload.received_by;
        delete payload.acknowledgement_no;
      }

      // Convert to FormData
      const formDataToSend = new FormData();
      Object.keys(payload).forEach((key) => {
        if (payload[key] !== undefined && payload[key] !== null && payload[key] !== "") {
          formDataToSend.append(key, payload[key]);
        }
      });
      if (attachment) {
        formDataToSend.append("file", attachment);
      }

      const isEditing = !!editingRecord;
      let result;
      if (isEditing) {
        const updateFn = registerType === "inward" ? updateInward : updateOutward;
        result = await updateFn(editingRecord.id, formDataToSend, csrfToken, { scope: apiScope });
      } else {
        const createFn = registerType === "inward" ? createInward : createOutward;
        result = await createFn(formDataToSend, csrfToken);
      }

      if (result.error) {
        let errorMsg = typeof result.error.error === "string" 
          ? result.error.error 
          : (typeof result.error.message === "string" ? result.error.message : `Failed to create ${registerType} entry`);
        setErrorDetails(result.error.details && typeof result.error.details === "object" ? result.error.details : {});
        
        setError(errorMsg);
        setTimeout(() => alertRef.current?.focus(), 0);
        return;
      }
      
      onSuccess?.(`${registerType === "inward" ? "Inward" : "Outward"} entry ${isEditing ? "updated" : "created"} successfully!`);
      if (isEditing) {
        onCancelEdit?.();
      } else {
        handleReset();
      }
    } catch (err) {
      setError(err.message || "An unexpected error occurred");
      setTimeout(() => alertRef.current?.focus(), 0);
    } finally {
      setLoading(false);
    }
  };

  const ALERT_ID = "inward-outward-form-alert";

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
        <GlobalButton 
          variant={registerType === "inward" ? "primary" : "ghost"}
          onClick={() => { setRegisterType("inward"); if (!editingRecord) handleReset(); }}
          disabled={!!editingRecord}
        >
          Inward Register
        </GlobalButton>
        <GlobalButton 
          variant={registerType === "outward" ? "primary" : "ghost"}
          onClick={() => { setRegisterType("outward"); if (!editingRecord) handleReset(); }}
          disabled={!!editingRecord}
        >
          Outward Register
        </GlobalButton>
      </div>

      <GlobalFormShell
        legend={editingRecord ? `Edit ${registerType === "inward" ? "Inward" : "Outward"} Entry` : `New ${registerType === "inward" ? "Inward" : "Outward"} Entry`}
        showAlert={!!error}
        alertText={error}
        alertId={ALERT_ID}
        formAlertRef={alertRef}
        actions={
          <>
            <GlobalButton
              type="submit"
              variant="primary"
              disabled={loading}
              onClick={handleSubmit}
              loading={loading}
            >
              {editingRecord ? "Save Changes" : "Save Entry"}
            </GlobalButton>
            {editingRecord ? (
              <GlobalButton
                type="button"
                variant="ghost"
                onClick={() => onCancelEdit?.()}
                disabled={loading}
              >
                Cancel
              </GlobalButton>
            ) : (
              <GlobalButton
                type="button"
                variant="ghost"
                onClick={handleReset}
                disabled={loading}
              >
                Clear form
              </GlobalButton>
            )}
          </>
        }
      >
        <div className="gff-field--full" style={{ marginBottom: "-0.5rem" }}>
          <h4 style={{ margin: "0.5rem 0", color: "var(--ub-teal-dark)", fontSize: "0.85rem", fontWeight: 700 }}>
            {registerType === "inward" ? "Sender Information" : "Recipient Information"}
          </h4>
        </div>
        
        {registerType === "inward" ? (
          <>
            <GlobalTextField
              id="date_received"
              label="Date Received"
              type="date"
              value={formData.date_received}
              onChange={(v) => setFormField("date_received", v)}
              error={errorDetails.date_received}
              baseAlertId={ALERT_ID}
              required
              max={new Date().toISOString().split("T")[0]}
            />
            <GlobalTextField
              id="from_name"
              label="From Name"
              value={formData.from_name}
              onChange={(v) => setFormField("from_name", v)}
              error={errorDetails.from_name}
              baseAlertId={ALERT_ID}
              required
            />
            <GlobalTextAreaField
              classNames={{ fieldWrap: "gff-field gff-field--full" }}
              id="from_address"
              label="From Address"
              value={formData.from_address}
              onChange={(v) => setFormField("from_address", v)}
              error={errorDetails.from_address}
              baseAlertId={ALERT_ID}
              maxLength={INWARD_OUTWARD_TEXT_MAX}
              required
            />
          </>
        ) : (
          <>
            <GlobalTextField
              id="date_sent"
              label="Date Sent"
              type="date"
              value={formData.date_sent}
              onChange={(v) => setFormField("date_sent", v)}
              error={errorDetails.date_sent}
              baseAlertId={ALERT_ID}
              required
              max={new Date().toISOString().split("T")[0]}
            />
            <GlobalTextField
              id="to_name"
              label="To Name"
              value={formData.to_name}
              onChange={(v) => setFormField("to_name", v)}
              error={errorDetails.to_name}
              baseAlertId={ALERT_ID}
              nameTextRules
              maxLength={30}
              required
            />
            <GlobalTextAreaField
              classNames={{ fieldWrap: "gff-field gff-field--full" }}
              id="to_address"
              label="To Address"
              value={formData.to_address}
              onChange={(v) => setFormField("to_address", v)}
              error={errorDetails.to_address}
              baseAlertId={ALERT_ID}
              maxLength={INWARD_OUTWARD_TEXT_MAX}
              required
            />
          </>
        )}

        <div className="gff-field--full" style={{ marginBottom: "-0.5rem", marginTop: "0.5rem" }}>
          <h4 style={{ margin: "0.5rem 0", color: "var(--ub-teal-dark)", fontSize: "0.85rem", fontWeight: 700 }}>
            Document Details
          </h4>
        </div>

        <GlobalSelectField
          id="document_type_id"
          label="Document Type"
          value={formData.document_type_id}
          onChange={(v) => setFormField("document_type_id", v ? Number(v) : "")}
          options={masterSelectOptions(lookups.documentTypes, {
            labelKey: "type_name",
            selectedValue: formData.document_type_id,
          })}
          error={errorDetails.document_type_id}
          baseAlertId={ALERT_ID}
          required
        />

        <GlobalSelectField
          id="dispatch_mode_id"
          label="Dispatch Mode"
          value={formData.dispatch_mode_id}
          onChange={(v) => setFormField("dispatch_mode_id", v ? Number(v) : "")}
          options={masterSelectOptions(lookups.dispatchModes, {
            labelKey: "mode_name",
            selectedValue: formData.dispatch_mode_id,
          })}
          error={errorDetails.dispatch_mode_id}
          baseAlertId={ALERT_ID}
          required
        />

        <GlobalTextAreaField
          classNames={{ fieldWrap: "gff-field gff-field--full" }}
          id="subject"
          label="Subject"
          value={formData.subject}
          onChange={(v) => setFormField("subject", v)}
          error={errorDetails.subject}
          baseAlertId={ALERT_ID}
          maxLength={INWARD_OUTWARD_TEXT_MAX}
          required
        />
        
        {registerType === "outward" && (
           <GlobalTextField
             id="acknowledgement_no"
             label="Acknowledgement No"
             value={formData.acknowledgement_no}
             onChange={() => {}}
             error={errorDetails.acknowledgement_no}
             baseAlertId={ALERT_ID}
             placeholder={editingRecord ? "" : "Auto-generated on save"}
             disabled
           />
        )}
        
        <div className="gff-field--full" style={{ marginBottom: "-0.5rem", marginTop: "0.5rem" }}>
          <h4 style={{ margin: "0.5rem 0", color: "var(--ub-teal-dark)", fontSize: "0.85rem", fontWeight: 700 }}>
            Additional Information
          </h4>
        </div>

        {registerType === "inward" ? (
          <UserSearch
            key="inward-received-by"
            id="received_by"
            label="Received By (Search)"
            value={formData.received_by}
            onChange={(v) => setFormField("received_by", v ? Number(v) : "")}
            selectedUserLabel={editingRecord?.received_by_name || editingRecord?.name}
            error={errorDetails.received_by}
            baseAlertId={ALERT_ID}
            required
          />
        ) : (
          <UserSearch
            key="outward-dispatched-by"
            id="dispatched_by"
            label="Dispatched By (Search)"
            value={formData.dispatched_by}
            onChange={(v) => setFormField("dispatched_by", v ? Number(v) : "")}
            selectedUserLabel={editingRecord?.dispatched_by_name || editingRecord?.name}
            error={errorDetails.dispatched_by}
            baseAlertId={ALERT_ID}
            required
          />
        )}
        
        <p className="gff-field gff-field--full">
          <label className={`gff-label${errorDetails.attachment ? " gff-label--error" : ""}`} htmlFor="attachment">
            Attachment {!editingRecord ? <span aria-hidden="true">*</span> : null}
          </label>
          <div
            className={`gff-input${errorDetails.attachment ? " gff-input--error" : ""}`}
            style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.35rem 0.5rem" }}
          >
            <input
              id="attachment"
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/png,image/webp"
              onChange={async (e) => {
                const file = e.target.files?.[0] || null;
                if (!file) {
                  setAttachment(null);
                  return;
                }
                if (!ALLOWED_UPLOAD_TYPES.includes(file.type)) {
                  setAttachment(null);
                  e.target.value = "";
                  setErrorDetails((prev) => ({ ...prev, attachment: INVALID_UPLOAD_TYPE_MESSAGE }));
                  return;
                }
                try {
                  const uploadFile = await compressAttachmentIfImage(file);
                  setAttachment(uploadFile);
                  if (errorDetails.attachment) setErrorDetails((prev) => ({ ...prev, attachment: "" }));
                } catch {
                  setAttachment(null);
                  e.target.value = "";
                  setErrorDetails((prev) => ({ ...prev, attachment: "Could not compress image" }));
                }
              }}
              required={!editingRecord}
              aria-invalid={errorDetails.attachment ? "true" : "false"}
              aria-describedby={errorDetails.attachment ? "attachment-error" : undefined}
            />
            <GlobalButton type="button" variant="ghost" onClick={() => fileInputRef.current?.click()}>
              Choose File
            </GlobalButton>
            <span className="gff-help">
              {attachment ? (
                <>New file selected: {attachment.name}</>
              ) : editingRecord?.attachment ? (
                <a
                  href={buildAttachmentUrl(editingRecord.attachment)}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#15803d" }}
                >
                  {getAttachmentName(editingRecord.attachment)}
                </a>
              ) : (
                "No file chosen"
              )}
            </span>
          </div>
          {errorDetails.attachment ? (
            <span id="attachment-error" className="gff-error" role="alert">
              {errorDetails.attachment}
            </span>
          ) : null}
        </p>

        <GlobalTextAreaField
          classNames={{ fieldWrap: "gff-field gff-field--full" }}
          id="remarks"
          label="Remarks"
          value={formData.remarks}
          onChange={(v) => setFormField("remarks", v)}
          error={errorDetails.remarks}
          baseAlertId={ALERT_ID}
          maxLength={INWARD_OUTWARD_TEXT_MAX}
          required
        />
      </GlobalFormShell>
    </div>
  );
};

export default RegisterEntryForm;
